import React from "react";
import { Route, Routes as _Routes } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { RoutesMap } from "RoutesMap";
import { FeatureFlags } from "contexts/ConfigurationProvider/FeatureFlags";
import { Layout, PaddedRouteArea } from "shared/Layout";
import { subscriptionGroupUserGrantsApi } from "client/api/UserGrantApi";
import { StripTrailingSlash } from "components/StripTrailingSlash";
import { AddUserGrantView } from "components/UserGrants/AddUserGrantView";
import { FeatureFlaggedRoute } from "components/authorization/FeatureFlaggedRoute";
import { ProtectedRoute } from "components/authorization/ProtectedRoute";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { NotFoundPage } from "pages/NotFoundPage";
import { SignOutPage } from "pages/SignOutPage";
import { SubscriptionPage } from "pages/subscriptions/SubscriptionPage";
import { BillingAccountsPage } from "areas/billing-accounts/BillingAccountsPage";
import { billingAccountRoutes } from "areas/billing-accounts/billing-account-routes";
import { BillingAccountDetailLayout } from "areas/billing-accounts/detail/BillingAccountDetailLayout";
import { BillingAccountDetails } from "areas/billing-accounts/detail/BillingAccountDetails";
import { BillingAccountHistory } from "areas/billing-accounts/detail/BillingAccountHistory";
import { BillingAccountSubscriptions } from "areas/billing-accounts/detail/BillingAccountSubscriptions";
import {
  CloudSubscriptionBySerialRedirect,
  CloudSubscriptionRedirect,
} from "areas/cloud-subscriptions/CloudSubscriptionRedirect";
import { cloudSubscriptionRoutes } from "areas/cloud-subscriptions/cloud-subscription-routes";
import { ServerLicenseBySerialRedirect, ServerLicenseRedirect } from "areas/licenses/ServerLicenseRedirect";
import { licenseRoutes } from "areas/licenses/license-routes";
import { OrdersPage } from "areas/orders/OrdersPage";
import { OrderBuilderPage } from "areas/orders/builder/OrderBuilderPage";
import { OrderVersionHistoryPage } from "areas/orders/detail/OrderVersionHistoryPage";
import { QuoteDocumentPage } from "areas/orders/document/QuoteDocumentPage";
import { orderRoutes } from "areas/orders/order-routes";
import { RateCardsPage } from "areas/rate-cards/RateCardsPage";
import { RateCardDetailLayout } from "areas/rate-cards/detail/RateCardDetailLayout";
import { RateCardDetails } from "areas/rate-cards/detail/RateCardDetails";
import { rateCardRoutes } from "areas/rate-cards/rate-card-routes";
import { SubscriptionAddExistingSubscriptionGroup } from "areas/subscriptions/detail/SubscriptionAddExistingSubscriptionGroup";
import { SubscriptionAddNewSubscriptionGroup } from "areas/subscriptions/detail/SubscriptionAddNewSubscriptionGroup";
import { SubscriptionAddUserAccess } from "areas/subscriptions/detail/SubscriptionAddUserAccess";
import { SubscriptionDeactivationHistory } from "areas/subscriptions/detail/SubscriptionDeactivationHistory";
import { SubscriptionDetailLayout } from "areas/subscriptions/detail/SubscriptionDetailLayout";
import { SubscriptionDetails } from "areas/subscriptions/detail/SubscriptionDetails";
import { SubscriptionHistory } from "areas/subscriptions/detail/SubscriptionHistory";
import { SubscriptionIpAddressAllowList } from "areas/subscriptions/detail/SubscriptionIpAddressAllowList";
import { SubscriptionLicenseEmails } from "areas/subscriptions/detail/SubscriptionLicenseEmails";
import { SubscriptionRateCard } from "areas/subscriptions/detail/SubscriptionRateCard";
import { SubscriptionUserAccess } from "areas/subscriptions/detail/SubscriptionUserAccess";
import { SubscriptionCreateForm } from "areas/subscriptions/detail/add/SubscriptionCreateForm";
import { SubscriptionEditForm } from "areas/subscriptions/detail/edit/SubscriptionEditForm";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";
import { userRoutes } from "areas/users/UserRoutes";

export function Routes() {
  return (
    <>
      <StripTrailingSlash />
      <_Routes>
        <Route element={<Layout />}>
          <Route index element={<Navigate to={subscriptionRoutes.root} />} />
          <Route path={licenseRoutes.root} element={<ProtectedRoute policies={RbacPolicies.ServerLicenses.Read} />}>
            <Route element={<PaddedRouteArea />}>
              <Route index element={<Navigate to={subscriptionRoutes.root} replace />} />
              <Route
                path={licenseRoutes.loadBySerial(":serialId").root}
                element={
                  <ProtectedRoute
                    policies={RbacPolicies.ServerLicenses.Read}
                    element={<ServerLicenseBySerialRedirect />}
                  />
                }
              />
              <Route
                path={`${licenseRoutes.detail(":licenseId").root}/*`}
                element={
                  <ProtectedRoute policies={RbacPolicies.ServerLicenses.Read} element={<ServerLicenseRedirect />} />
                }
              />
            </Route>
          </Route>
          <Route
            path={cloudSubscriptionRoutes.root}
            element={<ProtectedRoute policies={RbacPolicies.CloudSubscriptions.Read} />}
          >
            <Route element={<PaddedRouteArea />}>
              <Route index element={<Navigate to={subscriptionRoutes.root} replace />} />
              <Route
                path={cloudSubscriptionRoutes.loadBySerial(":serialId").root}
                element={
                  <ProtectedRoute
                    policies={RbacPolicies.CloudSubscriptions.Read}
                    element={<CloudSubscriptionBySerialRedirect />}
                  />
                }
              />
              <Route
                path={`${cloudSubscriptionRoutes.detail(":cloudSubscriptionId").root}/*`}
                element={
                  <ProtectedRoute
                    policies={RbacPolicies.CloudSubscriptions.Read}
                    element={<CloudSubscriptionRedirect />}
                  />
                }
              />
            </Route>
          </Route>
          <Route path={subscriptionRoutes.root} element={<ProtectedRoute policies={RbacPolicies.Subscriptions.Read} />}>
            <Route
              index
              element={<ProtectedRoute policies={RbacPolicies.Subscriptions.Read} element={<SubscriptionPage />} />}
            />
            <Route element={<PaddedRouteArea />}>
              <Route
                path={subscriptionRoutes.add}
                element={
                  <ProtectedRoute
                    policies={RbacPolicies.Subscriptions.ReadWrite}
                    element={<SubscriptionCreateForm />}
                  />
                }
              />
              <Route
                path={subscriptionRoutes.detail(":subscriptionId").root}
                element={
                  <ProtectedRoute policies={RbacPolicies.Subscriptions.Read} element={<SubscriptionDetailLayout />} />
                }
              >
                <Route
                  index
                  element={
                    <ProtectedRoute policies={RbacPolicies.Subscriptions.Read} element={<SubscriptionDetails />} />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").edit}
                  element={
                    <ProtectedRoute
                      policies={RbacPolicies.Subscriptions.ReadWrite}
                      element={<SubscriptionEditForm />}
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").history}
                  element={
                    <ProtectedRoute policies={RbacPolicies.Subscriptions.Read} element={<SubscriptionHistory />} />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").deactivationHistory}
                  element={
                    <ProtectedRoute
                      policies={RbacPolicies.Subscriptions.Read}
                      element={<SubscriptionDeactivationHistory />}
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").licenseEmails}
                  element={
                    <ProtectedRoute
                      policies={RbacPolicies.ServerLicenses.Read}
                      element={<SubscriptionLicenseEmails />}
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").userAccess}
                  element={
                    <ProtectedRoute
                      policies={[RbacPolicies.Users.Read, RbacPolicies.Subscriptions.Read]}
                      element={<SubscriptionUserAccess />}
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").addUserAccess}
                  element={
                    <ProtectedRoute
                      policies={[RbacPolicies.Users.Read, RbacPolicies.Subscriptions.ReadWrite]}
                      element={<SubscriptionAddUserAccess />}
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").addGroupUserAccess}
                  element={
                    <ProtectedRoute
                      policies={[RbacPolicies.Users.Read, RbacPolicies.SubscriptionGroups.ReadWrite]}
                      element={
                        <AddUserGrantView
                          assetIdParamName="subscriptionGroupId"
                          userGrantsApi={subscriptionGroupUserGrantsApi}
                          requiredWriteRole={RbacPolicies.SubscriptionGroups.ReadWrite}
                          roleType={"SubscriptionGroup"}
                        />
                      }
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").addNewSubscriptionGroup}
                  element={
                    <ProtectedRoute
                      policies={[RbacPolicies.Admin.ReadWrite]}
                      element={<SubscriptionAddNewSubscriptionGroup />}
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").ipAddressAllowList}
                  element={
                    <ProtectedRoute
                      policies={RbacPolicies.CloudSubscriptions.Read}
                      element={<SubscriptionIpAddressAllowList />}
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").rateCard}
                  element={
                    <ProtectedRoute
                      policies={RbacPolicies.RateCards.Read}
                      element={
                        <FeatureFlaggedRoute
                          featureFlag={FeatureFlags.AdoptionLedPricing}
                          element={<SubscriptionRateCard />}
                        />
                      }
                    />
                  }
                />
                <Route
                  path={subscriptionRoutes.detail(":subscriptionId").addExistingSubscriptionGroup}
                  element={
                    <ProtectedRoute
                      policies={[RbacPolicies.Admin.ReadWrite]}
                      element={<SubscriptionAddExistingSubscriptionGroup />}
                    />
                  }
                />
              </Route>
            </Route>
          </Route>
          <Route
            path={rateCardRoutes.root}
            element={
              <ProtectedRoute
                policies={RbacPolicies.RateCards.Read}
                element={<FeatureFlaggedRoute featureFlag={FeatureFlags.AdoptionLedPricing} />}
              />
            }
          >
            <Route
              index
              element={<ProtectedRoute policies={RbacPolicies.RateCards.Read} element={<RateCardsPage />} />}
            />
            <Route element={<PaddedRouteArea />}>
              <Route
                path={rateCardRoutes.detail(":rateCardCode").root}
                element={<ProtectedRoute policies={RbacPolicies.RateCards.Read} element={<RateCardDetailLayout />} />}
              >
                <Route
                  index
                  element={<ProtectedRoute policies={RbacPolicies.RateCards.Read} element={<RateCardDetails />} />}
                />
              </Route>
            </Route>
          </Route>
          <Route
            path={billingAccountRoutes.root}
            element={
              <ProtectedRoute
                policies={RbacPolicies.BillingAccounts.Read}
                element={<FeatureFlaggedRoute featureFlag={FeatureFlags.AdoptionLedPricing} />}
              />
            }
          >
            <Route
              index
              element={
                <ProtectedRoute policies={RbacPolicies.BillingAccounts.Read} element={<BillingAccountsPage />} />
              }
            />
            <Route element={<PaddedRouteArea />}>
              <Route
                path={billingAccountRoutes.detail(":billingAccountCode").root}
                element={
                  <ProtectedRoute
                    policies={RbacPolicies.BillingAccounts.Read}
                    element={<BillingAccountDetailLayout />}
                  />
                }
              >
                <Route
                  index
                  element={
                    <ProtectedRoute policies={RbacPolicies.BillingAccounts.Read} element={<BillingAccountDetails />} />
                  }
                />
                <Route
                  path={billingAccountRoutes.detail(":billingAccountCode").subscriptions}
                  element={
                    <ProtectedRoute
                      policies={RbacPolicies.BillingAccounts.Read}
                      element={<BillingAccountSubscriptions />}
                    />
                  }
                />
                <Route
                  path={billingAccountRoutes.detail(":billingAccountCode").history}
                  element={
                    <ProtectedRoute policies={RbacPolicies.BillingAccounts.Read} element={<BillingAccountHistory />} />
                  }
                />
              </Route>
            </Route>
          </Route>
          {userRoutes()}
          <Route
            path={orderRoutes.root}
            element={
              <ProtectedRoute
                policies={RbacPolicies.Orders.Read}
                element={<FeatureFlaggedRoute featureFlag={FeatureFlags.AdoptionLedPricing} />}
              />
            }
          >
            <Route index element={<ProtectedRoute policies={RbacPolicies.Orders.Read} element={<OrdersPage />} />} />
            <Route
              path={orderRoutes.detail(":odNumber").root}
              element={<ProtectedRoute policies={RbacPolicies.Orders.Read} element={<OrderBuilderPage />} />}
            />
            <Route
              path={orderRoutes.detail(":odNumber").history}
              element={<ProtectedRoute policies={RbacPolicies.Orders.Read} element={<OrderVersionHistoryPage />} />}
            />
            <Route
              path={orderRoutes.detail(":odNumber").document}
              element={<ProtectedRoute policies={RbacPolicies.Orders.Read} element={<QuoteDocumentPage />} />}
            />
          </Route>
        </Route>
        <Route path={RoutesMap.signOut} element={<SignOutPage />} />
        <Route path={RoutesMap.notFound} element={<NotFoundPage />} />
        <Route path={RoutesMap.fallback} element={<NotFoundPage />} />
      </_Routes>
    </>
  );
}
